import {customElement, property, queryAll} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {FormInputElement} from "./formInputElement";
import {elementFrom} from "../../../common/utils/html";
import type {DirectiveResult} from "lit/directive";
import type {Maybe} from "../../../bootstrap/common/types";
import {RADIO_ICON} from "../../../common/svgIcons";

export type DataRadioButton = {
    value: string;
    label: string;
    preset: boolean;
};

@customElement("eop-form-radio-buttons")
export class EopFormRadioButtons extends FormInputElement<string> {

    @property({attribute: "label"})
    public label: string;
    @property({attribute: "options", type: Array})
    public options: DataRadioButton[] = [];
    @queryAll("input")
    private inputs: NodeListOf<HTMLInputElement>;
    private iconNodes = new Map<number, Element>();

    public constructor() {
        super("");
    }

    public render(): TemplateResult {
        return html`
            <div class="input-element ${this.basicClassMap()}" ?required=${this.required}>
                <label class="label">
                    <span class="label-text">${this.label}</span>
                    ${this.labelSuffix()}
                </label>
                <div class="radio-buttons">
                    ${this.renderRadioButtons()}
                </div>
            </div>
            ${this.renderValidationMessages()}
        `;
    }

    public focusInput(): void {
        this.inputs.item(0)?.focus();
    }

    private renderRadioButtons(): DirectiveResult[] {
        return this.options.map((option, index) => {
            return html`
                <div class="radio-button">
                    <label>
                        <input type="radio"
                               .value=${option.value}
                               ?checked=${option.value === this.value}
                               ?required=${this.required}
                               @input=${this.select}
                               name=${this.getFullId()}
                               id="${this.getFullId()}-${index}"
                        >
                        <span class="icon">${this.iconNode(index)}</span>
                        <span class="radio-label">${option.label}</span>
                    </label>
                </div>
            `;
        });
    }

    protected preset(): Maybe<string> {
        return this.options
            .find(option => option.preset)
            ?.value;
    }

    protected valueEnhancements(inputValue: string): Map<string, string> {
        return new Map([["label", this.descriptorForValue(inputValue)]]);
    }

    private iconNode(index: number): Element {
        if (!this.iconNodes.has(index)) {
            this.iconNodes.set(index, elementFrom(RADIO_ICON));
        }
        return this.iconNodes.get(index)!;
    }

    private select(event: Event): void {
        const value = (event.currentTarget as HTMLInputElement).value;
        this.updateValue(value);
    }

    private descriptorForValue(value: string): string {
        return this.options
                .find(option => value === option.value)
                ?.label
            ?? "";
    }

}